<template>
    <div>
        <UiState
            v-if="loading"
            type="loading"
            :title="`Loading ${title || 'records'}`"
            compact
        />
        <UiState
            v-else-if="loadError"
            type="error"
            :message="loadError"
            actionLabel="Try again"
            compact
            @action="$emit('retry')"
        />
        <UiState
            v-else-if="!rows.length"
            type="empty"
            :title="`No ${title || 'records'} found`"
            compact
        />
        <vue-good-table
            v-else
            :mode="tableMode"
            :totalRows="totalRecords"
            :pagination-options="paginationOptions"
            :styleClass="`vgt-table striped condensed ${theme == themes.DARK ? 'dark' : ''}`"
            :columns="columns"
            :rows="rows"
            :search-options="searchOptions"
            @on-page-change="$emit('page-change', $event)"
            @on-per-page-change="$emit('per-page-change', $event)"
            @on-sort-change="$emit('sort-change', $event)"
            @on-search="$emit('search', $event)"
        >
            <template slot="table-row" slot-scope="props">
                <span v-if="props.column.field == 'actions'" class="action-buttons-container">
                    <AllowPermission :permission="`CMS_EDIT_${permissionCategory}`">
                        <Button
                            v-if="showEditBtn"
                            class="action-btn edit-btn"
                            :aria-label="`Edit ${title || 'record'}`"
                            :title="`Edit ${title || 'record'}`"
                            @click="$emit('edit', props)"
                        >
                            <i class="fas fa-edit mr-1"></i>
                            <span v-if="!onlyBtnIcons">Edit</span>
                        </Button>
                    </AllowPermission>

                    <AllowPermission :permission="customActionPermission" v-if="customActionPermission">
                        <Button
                            noMargin
                            v-if="customActionTitle"
                            :class="['action-btn', 'permissions-btn', { 'compact-action-btn': customActionIconOnly }]"
                            :aria-label="customActionTitle"
                            :title="customActionTitle"
                            :disabled="processingCustomAction"
                            @click="$emit('custom-action', props.row)"
                        >
                            <i :class="['fas', customActionIcon, { 'mr-1': !customActionIconOnly && !onlyBtnIcons }]"></i>
                            <span v-if="!onlyBtnIcons && !customActionIconOnly">
                                {{
                                    processingCustomActionId == props.row.id && processingCustomAction
                                        ? "Please wait..."
                                        : customActionTitle
                                }}
                            </span>
                        </Button>
                    </AllowPermission>
                    <Button
                        noMargin
                        v-if="customActionTitle && !customActionPermission"
                        :class="['action-btn', 'permissions-btn', { 'compact-action-btn': customActionIconOnly }]"
                        :aria-label="customActionTitle"
                        :title="customActionTitle"
                        :disabled="processingCustomAction"
                        @click="$emit('custom-action', props.row)"
                    >
                        <i :class="['fas', customActionIcon, { 'mr-1': !customActionIconOnly && !onlyBtnIcons }]"></i>
                        <span v-if="!onlyBtnIcons && !customActionIconOnly">
                            {{
                                processingCustomActionId == props.row.id && processingCustomAction
                                    ? "Please wait..."
                                    : customActionTitle
                            }}
                        </span>
                    </Button>

                    <AllowPermission :permission="`CMS_DELETE_${permissionCategory}`">
                        <Button
                            v-if="deleteRootUrl"
                            class="action-btn delete-btn"
                            :aria-label="`Delete ${title || 'record'}`"
                            :title="`Delete ${title || 'record'}`"
                            @click="$emit('delete', props.row.id)"
                        >
                            <i class="fas fa-trash mr-1"></i>
                            <span v-if="!onlyBtnIcons">Delete</span>
                        </Button>
                    </AllowPermission>
                </span>
                <span
                    v-else
                    :class="['crud-cell', { 'has-tooltip': props.column.tooltipField }]"
                    :title="props.column.tooltipField ? props.row[props.column.tooltipField] : null"
                >
                    <span
                        :class="[
                            'crud-cell-primary',
                            props.column.valueClassField ? props.row[props.column.valueClassField] : '',
                        ]"
                        :tabindex="props.column.tooltipField ? 0 : null"
                        :aria-label="props.column.tooltipField ? props.row[props.column.tooltipField] : null"
                    >
                        {{ props.formattedRow[props.column.field] }}
                    </span>
                    <small v-if="props.column.secondaryField" class="crud-cell-secondary">
                        {{ props.row[props.column.secondaryField] }}
                    </small>
                    <span v-if="props.column.tooltipField" class="crud-cell-tooltip" role="tooltip">
                        {{ props.row[props.column.tooltipField] }}
                    </span>
                </span>
            </template>
        </vue-good-table>
    </div>
</template>

<script>
import Button from "../../components/UI/Button.vue"
import UiState from "../../components/UI/UiState.vue"
import AllowPermission from "../layout/AllowPermission.vue"

import "vue-good-table/dist/vue-good-table.css"

export default {
    name: "CrudListTable",
    components: { Button, UiState, AllowPermission },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        loadError: {
            type: String,
            default: "",
        },
        title: {
            type: String,
            default: "",
        },
        rows: {
            type: Array,
            default: () => [],
        },
        columns: {
            type: Array,
            default: () => [],
        },
        totalRecords: {
            type: Number,
            default: 0,
        },
        paginationOptions: {
            type: Object,
            default: () => ({}),
        },
        tableMode: {
            type: String,
            default: "local",
        },
        searchOptions: {
            type: Object,
            default: () => ({}),
        },
        theme: {
            type: String,
            default: "",
        },
        themes: {
            type: Object,
            default: () => ({}),
        },
        permissionCategory: {
            type: String,
            default: "",
        },
        onlyBtnIcons: {
            type: Boolean,
            default: false,
        },
        showEditBtn: {
            type: Boolean,
            default: true,
        },
        deleteRootUrl: {
            type: String,
            default: "",
        },
        customActionPermission: {
            type: String,
            default: "",
        },
        customActionTitle: {
            type: String,
            default: "",
        },
        customActionIcon: {
            type: String,
            default: "fa-key",
        },
        customActionIconOnly: {
            type: Boolean,
            default: false,
        },
        processingCustomActionId: {
            type: [Number, String],
            default: null,
        },
        processingCustomAction: {
            type: Boolean,
            default: false,
        },
    },
}
</script>

<style lang="scss" scoped>
.crud-cell {
    position: relative;
    display: block;
    min-width: 0;
    font-size: 0.85em;
}

.crud-cell-primary {
    display: block;
}

.crud-cell-secondary {
    display: block;
    margin-top: 0.18rem;
    color: #748078;
    font-size: 0.68rem;
    font-weight: 600;
}

.crud-cell.has-tooltip .crud-cell-primary {
    cursor: help;
}

.crud-cell-tooltip {
    position: absolute;
    top: calc(100% + 0.4rem);
    left: 0;
    z-index: 8;
    width: max-content;
    max-width: 17rem;
    padding: 0.5rem 0.65rem;
    color: #fff;
    background: #24332d;
    border-radius: 0.35rem;
    box-shadow: 0 8px 20px rgba(25, 45, 37, 0.18);
    font-size: 0.72rem;
    line-height: 1.35;
    opacity: 0;
    pointer-events: none;
    transform: translateY(-0.2rem);
    transition: opacity 120ms ease, transform 120ms ease;
    visibility: hidden;
    white-space: normal;
}

.crud-cell.has-tooltip:hover .crud-cell-tooltip,
.crud-cell.has-tooltip:focus-within .crud-cell-tooltip {
    opacity: 1;
    transform: translateY(0);
    visibility: visible;
}
</style>
